import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { peekReferralGrant, redeemReferralGrant } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * GrantRedeemPage — landing for /r/grant/:token (admin-issued one-shot
 * referral links worth 30 days of PRO Player or PRO League).
 *
 * Flow:
 *  1. PEEK the token publicly to show what's being offered.
 *  2. If user is signed in → REDEEM button.
 *     If signed out → "Sign in to claim" CTA via the global auth modal.
 */
export default function GrantRedeemPage({ onRequireAuth, onToast }) {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [grant, setGrant] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [redeemed, setRedeemed] = useState(null);

  useEffect(() => {
    if (!token) return;
    peekReferralGrant(token)
      .then(setGrant)
      .catch((e) => setError(e?.response?.data?.detail || "This referral link is invalid."));
  }, [token]);

  const onRedeem = async () => {
    if (!user) {
      onRequireAuth?.();
      return;
    }
    setBusy(true);
    try {
      const r = await redeemReferralGrant(token);
      setRedeemed(r);
      await refresh?.();
      onToast?.(`${r.kind === "pro_league" ? "PRO League" : "PRO Player"} unlocked for ${r.days} days!`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't redeem.");
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="max-w-screen-md mx-auto px-6 py-16 text-center page-z">
        <h1 className="font-display text-3xl grad-text mb-3">REFERRAL LINK</h1>
        <p data-testid="grant-error" className="text-[#14b8a6]">{error}</p>
        <button
          type="button"
          onClick={() => navigate("/")}
          className="mt-6 px-4 py-2 rounded-2xl btn-ghost text-xs tracking-widest"
        >
          BACK TO HOME
        </button>
      </div>
    );
  }

  if (!grant) {
    return (
      <div className="max-w-screen-md mx-auto px-6 py-16 text-center page-z">
        <p className="text-sm text-white/50">Loading referral link…</p>
      </div>
    );
  }

  const isLeague = grant.kind === "pro_league";

  return (
    <div className="max-w-screen-md mx-auto px-6 py-16 page-z" data-testid="grant-page">
      <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
        <i className="fas fa-gift mr-1" />ONE-SHOT REFERRAL LINK
      </p>
      <h1 className="font-display text-4xl sm:text-5xl grad-text mt-1">
        {isLeague ? "PRO LEAGUE" : "PRO PLAYER"}
      </h1>
      <p className="text-sm text-white/70 mt-3">
        This link gives you <span className="text-[#14b8a6] font-bold">{grant.days} days</span> of{" "}
        <span className="text-[#14b8a6] font-bold">{isLeague ? "PRO League" : "PRO Player"}</span> on LEAGUE HUB —
        absolutely free. Stack it on any existing subscription.
      </p>

      {redeemed ? (
        <div className="mt-8 glass rounded-3xl p-6 border border-[#14b8a6]/40" data-testid="grant-success">
          <p className="text-xs tracking-[3px] text-[#14b8a6]">PERKS UNLOCKED</p>
          <p className="text-lg text-white mt-1">
            Expires <span className="text-[#14b8a6]">
              {new Date(redeemed.expires_at).toLocaleString()}
            </span>
          </p>
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            data-testid="grant-success-go-dashboard"
            className="mt-4 px-4 py-2 rounded-2xl text-xs btn-neon tracking-widest"
          >
            GO TO DASHBOARD <i className="fas fa-arrow-right ml-1" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={onRedeem}
          disabled={busy}
          data-testid="grant-redeem-btn"
          className="mt-8 px-6 py-3 rounded-2xl text-sm font-bold tracking-widest btn-neon disabled:opacity-50"
        >
          {user ? "CLAIM 30 DAYS FREE" : "SIGN IN TO CLAIM"}
        </button>
      )}

      <p className="text-[11px] text-white/40 mt-6">
        One-shot link — once redeemed, the token can't be used again. Expires{" "}
        {grant.expires_at ? new Date(grant.expires_at).toLocaleDateString() : "soon"}.
      </p>
    </div>
  );
}
